/**
 * Compliance Item form (Create / Edit).
 * Uses the shared TipTap editor for the rich-text description.
 */
import React, { useEffect, useState } from 'react';
import { X, Loader } from 'lucide-react';
import { complianceAPI } from '../../services/api';
import RichTextEditor from '../email/RichTextEditor';

const FREQUENCIES = [
  { value: 'biweekly', label: 'Every 15 days (Biweekly)' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi_annual', label: 'Semi-Annual (Every 6 months)' },
  { value: 'yearly', label: 'Yearly' },
];

// Common anchors — quick picks that fill the month+day fields. The label
// is stored so the UI can show "180d after FY End" cleanly.
const ANCHOR_PRESETS = [
  { label: 'FY End', month: 3, day: 31 },
  { label: 'Half-Year End', month: 9, day: 30 },
  { label: 'CY End', month: 12, day: 31 },
  { label: 'Q1 End', month: 6, day: 30 },
  { label: 'Q3 End', month: 12, day: 31 },
];

const ComplianceItemModal = ({ open, onClose, item, defaultSectionId, sections = [], onSaved }) => {
  const isEdit = !!item;
  const [saving, setSaving] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [form, setForm] = useState({
    section_id: defaultSectionId || '',
    name: '',
    description_html: '',
    default_assignee_id: '',
    compliance_type: 'recurring',
    frequency: 'monthly',
    due_date: '',
    due_date_rule: 'specific',
    anchor_month: '',
    anchor_day: '',
    days_after_anchor: '',
    anchor_label: '',
    internal_due_offset_days: '',
    event_trigger: '',
    event_due_days_after: '',
    custom_fields: [],
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    complianceAPI.listTeamMembers().then(r => setTeamMembers(r.data || []));
    if (isEdit && item) {
      setForm({
        section_id: item.section_id,
        name: item.name,
        description_html: item.description_html || '',
        default_assignee_id: item.default_assignee_id || '',
        compliance_type: item.compliance_type,
        frequency: item.frequency || 'monthly',
        due_date: item.due_date || '',
        due_date_rule: item.due_date_rule || 'specific',
        anchor_month: item.anchor_month ?? '',
        anchor_day: item.anchor_day ?? '',
        days_after_anchor: item.days_after_anchor ?? '',
        anchor_label: item.anchor_label || '',
        internal_due_offset_days: item.internal_due_offset_days ?? '',
        event_trigger: item.event_trigger || '',
        event_due_days_after: item.event_due_days_after ?? '',
        custom_fields: item.custom_fields || [],
      });
    } else {
      setForm(f => ({ ...f, section_id: defaultSectionId || f.section_id }));
    }
  }, [open, item, defaultSectionId]);

  if (!open) return null;

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addCustomField = () => {
    setF('custom_fields', [...(form.custom_fields || []), { key: '', label: '', field_type: 'text' }]);
  };

  const updateCustom = (idx, patch) => {
    const next = [...(form.custom_fields || [])];
    next[idx] = { ...next[idx], ...patch };
    setF('custom_fields', next);
  };

  const removeCustom = (idx) => {
    setF('custom_fields', (form.custom_fields || []).filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setError(null);
    if (!form.section_id || !form.name.trim()) {
      setError('Section and Name are required.');
      return;
    }
    if (form.compliance_type === 'recurring') {
      if (!form.frequency) {
        setError('Recurring items need a Frequency.');
        return;
      }
      if (form.due_date_rule === 'relative') {
        if (!form.anchor_month || !form.anchor_day || form.days_after_anchor === '') {
          setError('Relative schedule needs an anchor date and days after.');
          return;
        }
      } else if (!form.due_date) {
        setError('Recurring items need a First Due Date (or switch to relative schedule).');
        return;
      }
    }
    if (form.compliance_type === 'one_time' && !form.due_date) {
      setError('One-time items need Due Date.');
      return;
    }
    if (form.compliance_type === 'event_based' && !form.event_trigger.trim()) {
      setError('Event-based items need an Event Trigger.');
      return;
    }

    const payload = {
      ...form,
      default_assignee_id: form.default_assignee_id || null,
      due_date_rule: form.due_date_rule || 'specific',
      anchor_month: form.anchor_month === '' ? null : Number(form.anchor_month),
      anchor_day: form.anchor_day === '' ? null : Number(form.anchor_day),
      days_after_anchor: form.days_after_anchor === '' ? null : Number(form.days_after_anchor),
      anchor_label: form.anchor_label || null,
      internal_due_offset_days: form.internal_due_offset_days === '' ? null : Number(form.internal_due_offset_days),
      event_due_days_after: form.event_due_days_after === '' ? null : Number(form.event_due_days_after),
      // Trim empty custom fields
      custom_fields: (form.custom_fields || []).filter(cf => cf.key && cf.label),
    };
    // Relative mode: don't send a stale explicit due_date; backend derives it.
    if (form.compliance_type === 'recurring' && form.due_date_rule === 'relative') {
      payload.due_date = null;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await complianceAPI.updateItem(item.id, payload);
      } else {
        await complianceAPI.createItem(payload);
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="compliance-item-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">{isEdit ? 'Edit Compliance Item' : 'New Compliance Item'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded px-3 py-2" data-testid="item-form-error">{error}</div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Section</label>
              <select value={form.section_id} onChange={e => setF('section_id', e.target.value)}
                      className="w-full border rounded px-3 py-2 text-sm" data-testid="item-section">
                <option value="">Choose section…</option>
                {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Compliance Type</label>
              <select value={form.compliance_type}
                      onChange={e => setF('compliance_type', e.target.value)}
                      className="w-full border rounded px-3 py-2 text-sm" data-testid="item-type">
                <option value="recurring">Recurring</option>
                <option value="event_based">Event-based</option>
                <option value="one_time">One-time</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Compliance / Task Name</label>
            <input type="text" value={form.name} onChange={e => setF('name', e.target.value)}
                   className="w-full border rounded px-3 py-2 text-sm" data-testid="item-name"
                   placeholder="e.g. Quarterly SEBI Filing" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <RichTextEditor
              value={form.description_html}
              onChange={html => setF('description_html', html)}
              placeholder="Describe the compliance…"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Default Assignee</label>
              <select value={form.default_assignee_id}
                      onChange={e => setF('default_assignee_id', e.target.value)}
                      className="w-full border rounded px-3 py-2 text-sm" data-testid="item-assignee">
                <option value="">— None —</option>
                {teamMembers.map(m => <option key={m.id} value={m.id}>{m.name} ({m.email})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Internal Due (days before Due Date)</label>
              <input type="number" min="0" value={form.internal_due_offset_days}
                     onChange={e => setF('internal_due_offset_days', e.target.value)}
                     className="w-full border rounded px-3 py-2 text-sm"
                     placeholder="e.g. 7"
                     data-testid="item-internal-due" />
            </div>
          </div>

          {form.compliance_type === 'recurring' && (
            <div className="space-y-3 bg-blue-50 p-3 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Frequency</label>
                  <select value={form.frequency} onChange={e => setF('frequency', e.target.value)}
                          className="w-full border rounded px-3 py-2 text-sm" data-testid="item-frequency">
                    {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Due Date Rule</label>
                  <div className="inline-flex rounded-lg border overflow-hidden text-sm w-full">
                    <button type="button"
                            onClick={() => setF('due_date_rule', 'specific')}
                            className={`flex-1 px-3 py-2 ${form.due_date_rule === 'specific' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
                            data-testid="rule-specific">
                      Specific first date
                    </button>
                    <button type="button"
                            onClick={() => setF('due_date_rule', 'relative')}
                            className={`flex-1 px-3 py-2 border-l ${form.due_date_rule === 'relative' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
                            data-testid="rule-relative">
                      Days after a recurring date
                    </button>
                  </div>
                </div>
              </div>

              {form.due_date_rule === 'specific' ? (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">First Due Date</label>
                  <input type="date" value={form.due_date} onChange={e => setF('due_date', e.target.value)}
                         className="w-full md:w-1/2 border rounded px-3 py-2 text-sm" data-testid="item-due-date" />
                  <p className="text-xs text-blue-800 mt-1">
                    Instances auto-scheduled ~12 months forward from this date. The day-of-month is preserved.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-1.5" data-testid="anchor-presets">
                    {ANCHOR_PRESETS.map(p => (
                      <button key={p.label} type="button"
                              onClick={() => setForm(f => ({ ...f, anchor_month: p.month, anchor_day: p.day, anchor_label: p.label }))}
                              className={`px-2.5 py-1 text-xs rounded-full border transition ${
                                Number(form.anchor_month) === p.month && Number(form.anchor_day) === p.day
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'bg-white text-gray-700 hover:bg-blue-100 border-gray-300'
                              }`}
                              data-testid={`anchor-preset-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                        {p.label} ({p.day}-{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][p.month-1]})
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Anchor Month</label>
                      <select value={form.anchor_month}
                              onChange={e => setF('anchor_month', e.target.value ? Number(e.target.value) : '')}
                              className="w-full border rounded px-3 py-2 text-sm" data-testid="anchor-month">
                        <option value="">—</option>
                        {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                          <option key={i} value={i + 1}>{m}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Anchor Day</label>
                      <input type="number" min="1" max="31" value={form.anchor_day}
                             onChange={e => setF('anchor_day', e.target.value ? Number(e.target.value) : '')}
                             className="w-full border rounded px-3 py-2 text-sm" data-testid="anchor-day" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Days After</label>
                      <input type="number" min="0" value={form.days_after_anchor}
                             onChange={e => setF('days_after_anchor', e.target.value === '' ? '' : Number(e.target.value))}
                             className="w-full border rounded px-3 py-2 text-sm" data-testid="days-after-anchor"
                             placeholder="e.g. 180" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Anchor Label (optional)</label>
                      <input type="text" value={form.anchor_label}
                             onChange={e => setF('anchor_label', e.target.value)}
                             className="w-full border rounded px-3 py-2 text-sm" data-testid="anchor-label"
                             placeholder="e.g. FY End" />
                    </div>
                  </div>
                  <p className="text-xs text-blue-800">
                    E.g. "180 days after FY End": pick <b>FY End</b> (31-Mar) and enter <b>180</b> → first due 27-Sep.
                    Then recurs on the chosen Frequency from that date.
                  </p>
                </div>
              )}
            </div>
          )}

          {form.compliance_type === 'one_time' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Due Date</label>
              <input type="date" value={form.due_date} onChange={e => setF('due_date', e.target.value)}
                     className="w-full border rounded px-3 py-2 text-sm" data-testid="item-due-date" />
            </div>
          )}

          {form.compliance_type === 'event_based' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-amber-50 p-3 rounded-lg">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Trigger Event</label>
                <input type="text" value={form.event_trigger} onChange={e => setF('event_trigger', e.target.value)}
                       placeholder="e.g. AGM completion"
                       className="w-full border rounded px-3 py-2 text-sm"
                       data-testid="item-event-trigger" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Days after event (to complete by)</label>
                <input type="number" min="0" value={form.event_due_days_after}
                       onChange={e => setF('event_due_days_after', e.target.value)}
                       className="w-full border rounded px-3 py-2 text-sm"
                       data-testid="item-event-days" />
              </div>
              <p className="md:col-span-2 text-xs text-amber-800">
                When the event actually happens, open the pending occurrence in the Tracker and set its Due Date.
              </p>
            </div>
          )}

          {/* Custom fields */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-700">Custom Fields (optional)</label>
              <button onClick={addCustomField} className="text-xs text-blue-600 hover:text-blue-800" data-testid="add-custom-field-btn">
                + Add custom field
              </button>
            </div>
            {(form.custom_fields || []).map((cf, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 mb-2 text-sm">
                <input type="text" placeholder="key (slug)" value={cf.key}
                       onChange={e => updateCustom(i, { key: e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase() })}
                       className="col-span-4 border rounded px-2 py-1" />
                <input type="text" placeholder="Display label" value={cf.label}
                       onChange={e => updateCustom(i, { label: e.target.value })}
                       className="col-span-4 border rounded px-2 py-1" />
                <select value={cf.field_type} onChange={e => updateCustom(i, { field_type: e.target.value })}
                        className="col-span-3 border rounded px-2 py-1">
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="yes_no">Yes / No</option>
                </select>
                <button onClick={() => removeCustom(i)} className="col-span-1 text-red-600 hover:text-red-800" title="Remove">
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 border rounded text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="save-item-btn">
            {saving && <Loader size={14} className="animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Item'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComplianceItemModal;
